import { api } from "@repo/convex/api";
import type { Id } from "@repo/convex/dataModel";
import type { Editor } from "@tiptap/react";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { useObservedAction } from "@/hooks/use-observed-convex";
import { trackVoiceEvent } from "@/lib/analytics/domain";
import type { AnalyticsSurface } from "@/lib/analytics/events";
import { bucketDurationMs } from "@/lib/analytics/properties";
import { getErrorMessage } from "@/lib/app-errors";
import {
  MAX_VOICE_RECORDING_MS,
  MAX_VOICE_WALL_CLOCK_MS,
  useVoiceRecorder,
} from "./use-voice-recorder";

export type VoiceComposerSource =
  | "claudia-composer"
  | "document-editor"
  | "clarification"
  | "ask-user";

const MAX_VOICE_AUDIO_BYTES = 8 * 1024 * 1024;

export type VoiceComposerPhase = "idle" | "recording" | "transcribing";

export interface UseVoiceComposerOptions {
  editor: Editor | null;
  projectId?: Id<"projects"> | null;
  threadId?: string | null;
  source: VoiceComposerSource;
  surface?: AnalyticsSurface;
  disabled?: boolean;
  /** Document editor: capture the cursor on start and insert the transcript there. */
  snapshotSelectionOnStart?: boolean;
  /** Document editor: make the editor read-only while recording so the snapshot can't move. */
  lockEditorWhileRecording?: boolean;
  onTranscriptionCreated?: (transcriptionId: Id<"voiceTranscriptions">) => void;
}

export interface UseVoiceComposer {
  phase: VoiceComposerPhase;
  /** recording (incl. the stopping tail) — the strip is shown for this. */
  isRecording: boolean;
  isStopping: boolean;
  isTranscribing: boolean;
  /** Auto-paused on silence: the waveform is frozen and the caption changes. */
  isPaused: boolean;
  elapsedMs: number;
  /** Latest RMS level (0–1). */
  level: number;
  /** Rolling waveform buffer (oldest → newest, length WAVEFORM_BAR_COUNT). */
  levels: number[];
  /** True when voice input cannot run (disabled, no editor/project, missing thread). */
  unavailable: boolean;
  start: () => void;
  /** Discard (✕) — stop and throw the recording away, no transcription. */
  discard: () => void;
  /** Accept (✓) — stop, transcribe, and insert the result into the editor for review. */
  accept: () => Promise<void>;
}

function insertTranscript(editor: Editor, transcript: string, position?: number | null) {
  const insertPosition = position ?? editor.state.selection.from;
  const textBefore = editor.state.doc.textBetween(
    Math.max(0, insertPosition - 1),
    insertPosition,
    "",
    ""
  );
  const needsLeadingSpace = Boolean(textBefore && !/\s/.test(textBefore));
  const content = `${needsLeadingSpace ? " " : ""}${transcript}`;
  if (position === null || position === undefined) {
    editor.chain().focus().insertContent(content).run();
    return;
  }

  editor
    .chain()
    .focus()
    .insertContentAt(position, content)
    .setTextSelection(position + content.length)
    .run();
}

/**
 * Controller for voice dictation in a rich-text surface. Owns the recorder, the
 * transcription action, transcript insertion, the max-duration auto-stop, and error
 * toasts — so both the composer recording strip and the document-editor toolbar button
 * render from the same state instead of duplicating this logic.
 */
export function useVoiceComposer({
  editor,
  projectId,
  threadId,
  source,
  surface,
  disabled = false,
  snapshotSelectionOnStart = false,
  lockEditorWhileRecording = false,
  onTranscriptionCreated,
}: UseVoiceComposerOptions): UseVoiceComposer {
  const recorder = useVoiceRecorder();
  const transcribe = useObservedAction(api.voiceInput.transcribe, {
    feature: "voice-input",
    operation: "transcribe",
    includeRequestId: true,
    context: (args) => ({
      "project.id": args.projectId,
      "thread.id": args.threadId,
      "voice.source": args.source,
      "audio.bytes": args.audio.byteLength,
      "audio.duration_ms": args.audioDurationMs,
      "audio.wall_clock_ms": args.wallClockMs,
      "audio.mime_type": args.mimeType,
    }),
  });
  const [isTranscribing, setIsTranscribing] = useState(false);
  const snapshotPositionRef = useRef<number | null>(null);
  const startErrorToastRef = useRef<string | null>(null);

  const needsThread = source === "claudia-composer" || source === "ask-user";
  const unavailable = disabled || !editor || !projectId || (needsThread && !threadId);

  const unlockEditor = useCallback(() => {
    if (lockEditorWhileRecording) {
      editor?.setEditable(true);
    }
  }, [editor, lockEditorWhileRecording]);

  const start = useCallback(() => {
    if (unavailable) return;
    snapshotPositionRef.current = snapshotSelectionOnStart
      ? (editor?.state.selection.from ?? null)
      : null;
    if (lockEditorWhileRecording) {
      editor?.setEditable(false);
    }
    trackVoiceEvent("voice_recording_started", {
      source,
      surface,
      project_id: projectId,
      thread_id: threadId ?? undefined,
    });
    void recorder.start();
  }, [
    editor,
    lockEditorWhileRecording,
    projectId,
    recorder,
    snapshotSelectionOnStart,
    source,
    surface,
    threadId,
    unavailable,
  ]);

  const discard = useCallback(() => {
    recorder.cancel();
    unlockEditor();
    snapshotPositionRef.current = null;
  }, [recorder, unlockEditor]);

  const accept = useCallback(async () => {
    if (!projectId || !editor) return;

    const recording = await recorder.stop();
    if (!recording) {
      unlockEditor();
      snapshotPositionRef.current = null;
      toast.info("Recording was too short to transcribe.");
      return;
    }

    if (recording.audioBytes > MAX_VOICE_AUDIO_BYTES) {
      unlockEditor();
      snapshotPositionRef.current = null;
      toast.error("Recording is too large to transcribe.");
      return;
    }

    setIsTranscribing(true);
    try {
      const audio = await recording.blob.arrayBuffer();
      const result = await transcribe({
        projectId,
        source,
        threadId: threadId ?? undefined,
        audio,
        mimeType: recording.mimeType,
        audioDurationMs: recording.audioDurationMs,
        wallClockMs: recording.wallClockMs,
      });

      unlockEditor();
      insertTranscript(editor, result.transcript, snapshotPositionRef.current);
      snapshotPositionRef.current = null;
      onTranscriptionCreated?.(result.transcriptionId);
      trackVoiceEvent("voice_transcription_completed", {
        source,
        surface,
        project_id: projectId,
        duration_ms_bucket: bucketDurationMs(recording.audioDurationMs),
        thread_id: threadId ?? undefined,
      });
      if (source === "document-editor") {
        trackVoiceEvent("voice_transcript_used", {
          source,
          surface,
          project_id: projectId,
          target_action: "document_edit",
        });
      }
    } catch (error: unknown) {
      toast.error(getErrorMessage(error, "Voice transcription failed. Please try again."));
    } finally {
      unlockEditor();
      setIsTranscribing(false);
    }
  }, [
    editor,
    onTranscriptionCreated,
    projectId,
    recorder,
    source,
    surface,
    threadId,
    transcribe,
    unlockEditor,
  ]);

  // Surface recorder errors as a toast once (and unlock if we had locked the editor).
  useEffect(() => {
    if (recorder.status !== "error" || !recorder.error) {
      startErrorToastRef.current = null;
      return;
    }
    if (startErrorToastRef.current === recorder.error) return;
    startErrorToastRef.current = recorder.error;
    toast.error(recorder.error);
    unlockEditor();
  }, [recorder.error, recorder.status, unlockEditor]);

  // Hard caps: auto-accept (stop + transcribe) just before the audio/wall-clock limit.
  useEffect(() => {
    if (!recorder.isRecording || isTranscribing) return;
    if (
      recorder.elapsedMs >= MAX_VOICE_RECORDING_MS - 500 ||
      recorder.wallClockMs >= MAX_VOICE_WALL_CLOCK_MS - 500
    ) {
      void accept();
    }
  }, [accept, isTranscribing, recorder.elapsedMs, recorder.isRecording, recorder.wallClockMs]);

  // Esc while recording discards the take (same as the ✕ button). Document-level because the
  // recording strip replaces the input row, so the editor may not hold focus.
  useEffect(() => {
    if (!recorder.isRecording && !recorder.isStopping) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        discard();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [discard, recorder.isRecording, recorder.isStopping]);

  const isRecording = recorder.isRecording || recorder.isStopping;
  const phase: VoiceComposerPhase = isTranscribing
    ? "transcribing"
    : isRecording
      ? "recording"
      : "idle";

  return {
    phase,
    isRecording,
    isStopping: recorder.isStopping,
    isTranscribing,
    isPaused: recorder.isPausedForSilence,
    elapsedMs: recorder.elapsedMs,
    level: recorder.level,
    levels: recorder.levels,
    unavailable,
    start,
    discard,
    accept,
  };
}
